import React, { useState, useEffect, useRef } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js";
import Alert from '../common/Alert.js';
import loanReceivedService from '../../services/LoanReceivedService.js';
import {
  Plus, CreditCard, TrendingDown, TrendingUp,
  AlertTriangle, ChevronLeft, ChevronRight
} from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { useTranslation } from 'react-i18next';
import { listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';


const LoanReceivedList = () => {
  const { t } = useTranslation();
  const [loans, setLoans] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    lender_type: '',
    loan_type: '',
    search: '',
    overdue_only: false
  });
  const [loanTypes, setLoanTypes] = useState([]);
  const [lenderTypes, setLenderTypes] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const perPage = 10;

  useEffect(() => { fetchData(); }, [currentPage, filters]);
  useEffect(() => { fetchDropdownData(); }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchDropdownData = async () => {
    try {
      const [typesRes, lenderTypesRes] = await Promise.all([
        loanReceivedService.getLoanTypes(),
        loanReceivedService.getLenderTypes()
      ]);
      setLoanTypes(typesRes.data || []);
      setLenderTypes(lenderTypesRes.data || []);
    } catch (err) {
      console.error('Failed to load dropdown data', err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.lender_type) params.lender_type = filters.lender_type;
      if (filters.loan_type) params.loan_type = filters.loan_type;
      if (filters.search) params.search = filters.search;
      if (filters.overdue_only) params.overdue_only = 'true';

      const [loansRes, statsRes] = await Promise.all([
        loanReceivedService.getAll(currentPage, perPage, params),
        loanReceivedService.getStatistics(filters.lender_type || null, filters.status || null)
      ]);
      setLoans((prev) => mergeLoadedPage(currentPage, loansRes.data || [], prev));
      setTotalPages(loansRes.pagination?.pages || 1);
      setStatistics(statsRes.data || null);
    } catch (err) {
      Alert.error('Failed to load loan records');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({ status: '', lender_type: '', loan_type: '', search: '', overdue_only: false });
    setCurrentPage(1);
  };

  const hasActiveFilters = filters.status || filters.lender_type || filters.loan_type || filters.search || filters.overdue_only;

  const handleApprove = async (loanId, lenderName) => {
    setActiveDropdown(null);
    const confirmed = await Alert.confirm(`Approve loan from "${lenderName}"?`, 'Approve Loan');
    if (confirmed) {
      try {
        await loanReceivedService.approve(loanId);
        Alert.success('Loan approved successfully!');
        fetchData();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to approve loan');
      }
    }
  };

  const handleReject = async (loanId, lenderName) => {
    setActiveDropdown(null);
    const reason = await Alert.prompt(`Enter rejection reason for loan from "${lenderName}":`, 'Reject Loan');
    if (reason !== null) {
      try {
        await loanReceivedService.reject(loanId, reason || 'No reason provided');
        Alert.success('Loan rejected successfully!');
        fetchData();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to reject loan');
      }
    }
  };

  const handleActivate = async (loanId, lenderName) => {
    setActiveDropdown(null);
    const confirmed = await Alert.confirm(`Activate loan from "${lenderName}"?`, 'Activate Loan');
    if (confirmed) {
      try {
        await loanReceivedService.activate(loanId);
        Alert.success('Loan activated successfully!');
        fetchData();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to activate loan');
      }
    }
  };

  const handleDelete = async (loanId, lenderName) => {
    setActiveDropdown(null);
    const confirmed = await Alert.confirm(`Delete loan record from "${lenderName}"?`, 'Delete Loan');
    if (confirmed) {
      try {
        await loanReceivedService.delete(loanId);
        Alert.success('Loan deleted successfully!');
        if (loans.length === 1 && currentPage > 1) setCurrentPage(currentPage - 1);
        else fetchData();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete loan');
      }
    }
  };

  const toggleDropdown = (loanId) => {
    setActiveDropdown(activeDropdown === loanId ? null : loanId);
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount || 0);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusConfig = (status) => {
    const map = {
      pending:   { bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200',  dot: 'bg-amber-400',  label: 'Pending'   },
      approved:  { bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200',   dot: 'bg-blue-500',   label: 'Approved'  },
      active:    { bg: 'bg-emerald-50',text: 'text-emerald-700',border: 'border-emerald-200',dot: 'bg-emerald-500',label: 'Active'    },
      completed: { bg: 'bg-gray-50',   text: 'text-gray-600',   border: 'border-gray-200',   dot: 'bg-gray-400',   label: 'Completed' },
      rejected:  { bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',    dot: 'bg-red-500',    label: 'Rejected'  },
      defaulted: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500', label: 'Defaulted' },
      cancelled: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-400', label: 'Cancelled' }
    };
    return map[status] || map.pending;
  };

  const StatusBadge = ({ status }) => {
    const cfg = getStatusConfig(status);
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></span>
        {cfg.label}
      </span>
    );
  };

  // Build dropdown menu items based on loan status
  const getLoanReceivedExtraItems = (loan) => {
    const items = [];
    if (loan.status === 'pending') {
      items.push(
        { label: 'Approve Loan', icon: '✅', variant: 'success', onClick: () => handleApprove(loan.loan_id, loan.lender_name) },
        { label: 'Reject Loan', icon: '❌', variant: 'danger', onClick: () => handleReject(loan.loan_id, loan.lender_name) },
      );
    }
    if (loan.status === 'approved') {
      items.push(
        { label: 'Activate Loan', icon: '⚡', variant: 'primary', onClick: () => handleActivate(loan.loan_id, loan.lender_name) },
        { label: 'Add Repayment', icon: '💰', variant: 'success', onClick: () => navigate(`/loans-received/repayment/${loan.loan_id}`) },
      );
    }
    if (loan.status === 'active') {
      items.push({
        label: 'Add Repayment',
        icon: '💰',
        variant: 'success',
        onClick: () => navigate(`/loans-received/repayment/${loan.loan_id}`),
      });
    }
    return items;
  };

  const progressPercent = (loan) => {
    const total = loan.loan_amount > 0 ? loan.loan_amount : 1;
    return Math.min(100, Math.round((loan.amount_paid / total) * 100));
  };

  return (
    <DashboardLayout>

              <div className="mb-4 sm:mb-6">
                <ListPageHeader
                  title="Loans Received"
                  subtitle="Track and manage all received loans"
                  icon={CreditCard}
                  titleClassName="text-secondary"
                  iconClassName="w-6 h-6 text-buttonBg shrink-0"
                  className="mb-5"
                >
                  <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/loans-received/add')}>
                    Add Loan
                  </Button>
                </ListPageHeader>

                {/* Statistics Cards */}
                {statistics && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
                    {[
                      { label: 'Total Loans',  value: statistics.total_loans,                     isNum: true,  color: 'border-blue-500',   textColor: 'text-blue-600'   },
                      { label: 'Received',     value: formatCurrency(statistics.total_amount_received), isNum: false, color: 'border-green-500',  textColor: 'text-green-600'  },
                      { label: 'Paid',         value: formatCurrency(statistics.total_amount_paid),     isNum: false, color: 'border-orange-500', textColor: 'text-orange-600' },
                      { label: 'Pending',      value: formatCurrency(statistics.total_amount_pending),  isNum: false, color: 'border-red-500',    textColor: 'text-red-600'    },
                      { label: 'Active',       value: statistics.active_count,                    isNum: true,  color: 'border-yellow-500', textColor: 'text-yellow-600' },
                      { label: 'Overdue',      value: statistics.overdue_count,                   isNum: true,  color: 'border-purple-500', textColor: 'text-purple-600' },
                    ].map((stat, i) => (
                      <div key={i} className={`bg-white p-3 sm:p-4 rounded-xl shadow-sm border-l-4 ${stat.color}`}>
                        <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                        <p className={`font-bold ${stat.isNum ? 'text-xl sm:text-2xl' : 'text-sm sm:text-base'} ${stat.textColor}`}>{stat.value}</p>
                      </div>
                    ))}
                  </div>
                )}

                <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
                  <div>
                    <SearchSubmitInput
                      name="search"
                      value={filters.search}
                      onSearch={(value) => {
                        setFilters((prev) => ({ ...prev, search: value }))
                        setCurrentPage(1)
                      }}
                      placeholder="Search by name, ID..."
                      inputClassName="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                    />
                  </div>
                  <div>
                    <select name="lender_type" value={filters.lender_type} onChange={handleFilterChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm">
                      <option value="">All Lender Types</option>
                      {lenderTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <select name="status" value={filters.status} onChange={handleFilterChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm">
                      <option value="">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="rejected">Rejected</option>
                      <option value="defaulted">Defaulted</option>
                    </select>
                  </div>
                  <div>
                    <select name="loan_type" value={filters.loan_type} onChange={handleFilterChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm">
                      <option value="">All Loan Types</option>
                      {loanTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-xl cursor-pointer w-full">
                      <input type="checkbox" name="overdue_only" checked={filters.overdue_only}
                        onChange={handleFilterChange}
                        className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-blue-400" />
                      <span className="text-sm text-gray-700">Overdue Only</span>
                    </label>
                  </div>
                </ListFilterPanel>
              </div>

              {/* Content */}
              {loading && currentPage === 1 ? (
                <div className="flex items-center justify-center py-20">
                  <Loader fullScreen={false} size="default" message="Loading loans..." />
                </div>
              ) : loans.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                  <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No loan records found</p>
                  <p className="text-gray-400 text-sm mt-1">Try adjusting your filters or add a new loan</p>
                </div>
              ) : (
                <>
                  {/* ─── Desktop Table ─── */}
                  <div className="hidden lg:block bg-white rounded-xl shadow-sm overflow-hidden mb-4">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Loan ID</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Lender</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.type')}</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Loan Amount</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Pending</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Date</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {loans.map((loan) => (
                            <tr key={loan.loan_id} className="hover:bg-orange-50/30 transition-colors">
                              {/* Clickable Loan ID */}
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => navigate(`/loans-received/view/${loan.loan_id}`)}
                                  className={listIdLinkClass}
                                >
                                  {loan.loan_id}
                                </button>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-gray-900">{loan.lender_name}</div>
                                <div className="text-xs text-gray-400 capitalize mt-0.5">{loan.lender_type.replace(/_/g, ' ')}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 capitalize">{loan.loan_type.replace(/_/g, ' ')}</td>
                              <td className="px-4 py-3 text-sm font-semibold text-right text-gray-900">{formatCurrency(loan.loan_amount)}</td>
                              <td className="px-4 py-3 text-right">
                                <span className={`text-sm font-semibold ${loan.amount_pending > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                  {formatCurrency(loan.amount_pending)}
                                </span>
                                {loan.is_overdue && (
                                  <div className="flex items-center justify-end gap-1 mt-0.5">
                                    <AlertTriangle className="w-3 h-3 text-red-500" />
                                    <span className="text-xs text-red-500 font-semibold">Overdue</span>
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{formatDate(loan.due_date)}</td>
                              <td className="px-4 py-3 text-center"><StatusBadge status={loan.status} /></td>
                              {/* 3-dot Actions */}
                              <td className="px-4 py-3 text-center">
                                <RowActionsMenu
                                  isOpen={activeDropdown === loan.loan_id}
                                  onToggle={() => toggleDropdown(loan.loan_id)}
                                  onClose={() => setActiveDropdown(null)}
                                  menuRef={dropdownRef}
                                  onView={() => navigate(`/loans-received/view/${loan.loan_id}`)}
                                  onEdit={
                                    loan.status === 'pending'
                                      ? () => navigate(`/loans-received/edit/${loan.loan_id}`)
                                      : undefined
                                  }
                                  onDelete={
                                    loan.status === 'pending' || loan.status === 'rejected'
                                      ? () => handleDelete(loan.loan_id, loan.lender_name)
                                      : undefined
                                  }
                                  extraItems={getLoanReceivedExtraItems(loan)}
                                  className="relative inline-block"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ─── Mobile Cards ─── */}
                  <div className="lg:hidden space-y-3 mb-4">
                    {loans.map((loan) => (
                      <div key={loan.loan_id} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                        {/* Card Header */}
                        <div className="p-4 pb-3">
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="flex-1 min-w-0">
                              <button
                                onClick={() => navigate(`/loans-received/view/${loan.loan_id}`)}
                                className={listIdLinkMobileClass}
                              >
                                {loan.loan_id}
                              </button>
                              <p className="text-sm font-semibold text-gray-800 mt-0.5 truncate">{loan.lender_name}</p>
                              <p className="text-xs text-gray-400 capitalize">{loan.lender_type.replace(/_/g, ' ')} · {loan.loan_type.replace(/_/g, ' ')}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <StatusBadge status={loan.status} />
                              {loan.is_overdue && (
                                <span className="inline-flex items-center gap-1 text-xs text-red-600 font-semibold whitespace-nowrap">
                                  <AlertTriangle className="w-3 h-3" /> Overdue
                                </span>
                              )}
                              <RowActionsMenu
                                isOpen={activeDropdown === `m-${loan.loan_id}`}
                                onToggle={() => toggleDropdown(`m-${loan.loan_id}`)}
                                onClose={() => setActiveDropdown(null)}
                                menuRef={dropdownRef}
                                onView={() => navigate(`/loans-received/view/${loan.loan_id}`)}
                                onEdit={
                                  loan.status === 'pending'
                                    ? () => navigate(`/loans-received/edit/${loan.loan_id}`)
                                    : undefined
                                }
                                onDelete={
                                  loan.status === 'pending' || loan.status === 'rejected'
                                    ? () => handleDelete(loan.loan_id, loan.lender_name)
                                    : undefined
                                }
                                extraItems={getLoanReceivedExtraItems(loan)}
                                className="relative"
                              />
                            </div>
                          </div>

                          {/* Amounts */}
                          <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm mb-3">
                            <div className="bg-gray-50 rounded p-2.5">
                              <p className="text-xs text-gray-400 mb-0.5">Loan Amount</p>
                              <p className="text-sm font-bold text-gray-800">{formatCurrency(loan.loan_amount)}</p>
                            </div>
                            <div className="bg-red-50 rounded p-2.5">
                              <p className="text-xs text-gray-400 mb-0.5">Pending</p>
                              <p className={`text-sm font-bold ${loan.amount_pending > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                {formatCurrency(loan.amount_pending)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Due Date</p>
                              <p className="text-sm font-medium text-gray-800">{formatDate(loan.due_date)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Repaid</p>
                              <p className="text-sm font-medium text-emerald-600">{progressPercent(loan)}%</p>
                            </div>
                          </div>

                          {/* Progress bar */}
                          <div className="mb-0">
                            <div className="flex justify-between text-xs text-gray-400 mb-1">
                              <span>Progress</span>
                              <span>{progressPercent(loan)}%</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-1.5">
                              <div
                                className="bg-emerald-500 h-1.5 rounded-full transition-all"
                                style={{ width: `${progressPercent(loan)}%` }}
                              />
                            </div>
                          </div>
                        </div>

                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between bg-white rounded-xl shadow-sm px-4 py-3">
                      <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        loading={loading && currentPage > 1}
                        onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
                      />
                    </div>
                  )}
                </>
              )}
    </DashboardLayout>
  );
};

export default LoanReceivedList;